<template>
  <div id="Utilities">

    <p>
      För många personer med särskilda behov innebär digital teknik en stor hjälp i vardagen.

      Drygt <a href="https://internet.begripsam.se/#/stats/Smart%2520telefon%2520SMFOI%25202017/4365" target="_blank"> var fjärde person med funktionsnedsättning använder hjälpmedel till sin smarta telefon</a> och var femte till datorn, enligt Svenskarna med funktionsnedsättning och internet. Bland blinda personer anger 9 av 10 att de använder hjälpmedel till sin smarta telefon och nästan var femte att de är i behov av ytterligare hjälpmedel. Mer information om undersökningen och diskussion till begrepp och definitioner finns tillgängligt i projektets <a href="http://www.begripsam.se/internet/rapporter/" target="_blank">rapporter</a>.
    </p>

    <p>
      I detta kapitel presenteras grundläggande exempel på digitala hjälpmedel och tekniska lösningar såväl som nya spännande innovationer.
    </p>

    <h3>Kommunikation och stöd i vardagen</h3>

    <p>
       En stor och viktig del av vår vardag bygger på mellanmänsklig kommunikation, såväl i fysiska möten som i samtal och text- och bildmeddelanden via mobiltelefon och dator. En utbredd tillgång till internet och god uppkoppling har möjliggjort nya kommunikationslösningar för personer med olika funktionsnedsättningar, såsom videosamtal för att kommunicera med teckenspråk eller läppläsning. <a href="https://bildtelefoni.net/" target="_blank">Bildtelefoni.net</a> är en kostnadsfri tjänst som genom trepartssamtal och -video möjliggör för personer som använder svenskt teckenspråk och personer som använder svenskt tal att ringa till varandra, eller för personer som befinner sig på samma plats att få samtal distanstolkade. <a href="https://www.texttelefoni.se/" target="_blank">Texttelefoni.se</a> erbjuder på liknande sätt stöd för personer med hörselnedsättning, dövhet, dövblindhet, talsvårigheter eller som kommunicerar med text eller punktskrift att ringa samt ta emot samtal.
    </p>

    <p>
      Nästan <a href="https://internet.begripsam.se/#/stats/Dator%2520SMFOI%25202017/1100" target="_blank">två av tre blinda personer använder punktskriftsdisplayer</a> till sin dator, och surfplattor med <a href="https://store.humanware.com/hus/braillenote-touch-32.html" target="_blank">extern punktskriftdisplay</a> och <a href="http://blitab.com/" target="_blank">taktila skärmar</a> är utvecklade särskilt för denna användargrupp. För personer med <a href="https://www.1177.se/Stockholm/Fakta-och-rad/Sjukdomar/ALS" target="_blank">ALS</a>, <a href="https://www.1177.se/Stockholm/Fakta-och-rad/Sjukdomar/Autism/" target="_blank">autism</a>, <a href="https://www.1177.se/Stockholm/Fakta-och-rad/Sjukdomar/Cerebral-pares---CP/" target="_blank">CP</a> eller Retts syndrom är <a href="https://www.youtube.com/watch?v=hHTlOhDphIc" target="_blank">Tobiis kommunikationslösningar</a> designade för att styras med exempelvis touch eller ögonrörelser, och erbjuder nya möjligheter att <a href="https://www.youtube.com/watch?v=uVWlNyVby0I" target="_blank">kommunicera i vardagen</a>.
    </p>

    <p>
      Annan digital teknik hjälper personer med motorisk nedsatthet eller muskelryckningar att, med <a href="http://www.gyenno.com/spoon-en.html" target="_blank">inbyggd sensorteknik i besticken</a>, stabilisera rörelser då de äter mat.
    </p>

    <p>
      Enligt designprincipen <a-wiki c="Design_för_alla">Design för alla</a-wiki> bidrar utformning för personer med särskilda behov ofta stora fördelar även för övriga användare. Det är en bra riktlinje och princip att följa, men i de fall det inte är praktiskt möjligt att tillgodose allas behov som standard bör produkten eller tjänsten erbjuda möjligheter för särskild anpassning. Ett exempel är en <a href="https://www.dagbladet.no/tema/hemmelig-knapp-gir-mer-tid-over-gangfeltet/62203598" target="_blank">hemlig knapp för mer tid</a> vid vissa bevakade övergångsställen i Norge.
    </p>

    <p>
      Utöver kommersiella verktyg och lösningar från kommuner och myndigheter finns det även en betydande <a-wiki c="Makerrörelsen" target="_blank">makerkultur</a-wiki> som designar och utvecklar kraftfulla hjälpmedel. I det Berlinbaserade projektet <a href="http://www.madeformywheelchair.de/" target="_blank">Made for my wheelchair</a> har rullstolsanvändare, designers och ingenjörer samlats för att identifiera behov och utifrån dessa skapa <a-wiki c="Gör-det-själv">Gör-det-själv</a-wiki>-produkter och -tjänster som till en låg kostnad ger stort mervärde till användaren. Resultaten är fritt tillgängliga under öppen licens och är förhållandevis enkla att själv återskapa.
    </p>

    <p>
      Ett annat exempel på globala, idella krafter är danska <a href="https://www.bemyeyes.com/" target="_blank">Be my eyes</a>, en kostnadsfri mobilapp som kopplar samman  blinda och synskadade med seende volontärer för guidning genom vardagssituationer. Genom appen strömmar den synskadade en video av sin omgivning och den situation hen behöver assistans i, t.ex. att läsa skyltar på tågstationen, välja rätt produkt i matbutiken eller hitta örhänget som har tappats på golvet. Den seende personen, som ser videon direkt i sin egen telefon, beskriver vad som filmas och fungerar i praktiken som ett par virtuella ögon åt den synskadade.
    </p>

    <media-container class="mediaContainer">
      <div>
        <div class="content">
          <iframe src="https://www.youtube.com/embed/6GRfFuWsjNU" width="100%" height="360" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe>
        </div>
        <div class="caption">
          Be My Eyes kopplar samman blinda och synskadade med videoströmning, för assistans i vardagen.
        </div>
        <div class="source">
          Källa: <a href="https://www.youtube.com/watch?v=6GRfFuWsjNU" title="Youtube: Be My Eyes - Bringing Sight to Blind and Low Vision People" target="_blank">Be My Eyes (Youtube)</a>
        </div>
      </div>
    </media-container>

    <info-fields class="infoFields" id="hur-kommunicerar-du">

      <div class="excercise">

        <div class="hBox">
          Hur kommunicerar du?
        </div>

        <p>
          Reflektera över de verktyg och tjänster du använder för att kommunicera i vardagen.
        </p>

        <div class="hBox">
          DISKUTERA
        </div>

        <ul>
          <li>
            Vilka verktyg och tjänster använder du för att kommunicera med familj, vänner, företag och myndigheter?
          </li>
          <li>
            Hur använder du dessa verktyg och tjänster?
          </li>
          <li>
            Vilka personer kan ha svårt att använda dessa verktyg och tjänster?
          </li>
          <li>
            Hur kan verktygen och tjänsterna hjälpa personer med olika funktionsnedsättningar att kommunicera?
          </li>

        </ul>

      </div>

      <div class="excercise" id="var-nagons-ogon">

        <div class="hBox">
          Var någons ögon
        </div>

        <p>
          Tillsammans med en vän, använd videosamtal för att guida varandra genom rummet.
        </p>

        <ol>
          <li>
            Person A laddar hem appen <span class="semibold">Jitsi Meet</span> till sin mobil eller surfplatta, och går in i rummet: <random-string v-model="jitsi" class="semibold"></random-string>.
          </li>
          <li>
            Person B gör detsamma <i>eller</i> går in på webbplatsen <a :href="'https://meet.jit.si/'+jitsi" target="_blank">https://meet.jit.si/{{jitsi}}</a> på sin dator.
          </li>
          <li>
            Kontrollera att båda kan se och höra varandra i videosamtalet.
          </li>
          <li>
            Person A blundar och filmar omgivningen med sin mobiltelefon.
          </li>
          <li>
            Person B beskriver vad hen ser och ger instruktioner och enkla uppgifter för Person A att utföra.
          </li>
        </ol>

        <p>
          Obs! Undvik farliga situationer såsom trappor eller användning av verktyg.
        </p>

        <p>
          Det går även bra att använda andra tjänster för videosamtal, såsom WhatsApp, Skype, Facebook Messenger eller Apple FaceTime.
        </p>

        <div class="hBox">
          DISKUTERA
        </div>

        <ul>
          <li>
            Hur var upplevelsen att utföra enklare uppgifter som temporärt blind?
          </li>
          <li>
            I vilka situationer kan videosamtal vara till stöd blinda personer?
          </li>
          <li>
            Hur skulle motsvarande teknisk lösning designas för att hjälpa döva i vardagen?
          </li>
        </ul>

      </div>

    </info-fields>

    <h3>Tillgänglighet för datorer, telefoner och webbläsare</h3>

    <p>
      Många av de digitala produkter och tjänster vi använder dagligen har stöd för tillgänglighetsanpassning. Inbyggd funktionalitet i datorn och telefonen erbjuder ofta möjligheten att anpassa färger och textstorlek på skärmen för ökad läsbarhet, <a-wikt c="skärmläsare">skärmläsning</a-wikt> som använder <a-wiki c="Talsyntes">talsyntes</a-wiki> för uppläsning av text- och alternativt medieinnehåll, samt att med <a-wiki c="Taligenkänning">taligenkänning</a-wiki> låta användaren navigera och ge instruktioner med rösten.
    </p>

    <p>
      Det finns även ett stort urval av tillbehör som hjälper personer med olika behov att interagera med sin dator, såsom möss som automatiskt stabiliserar skakiga handrörelser, anpassningsbara mjuk- och hårdvarutangentbord samt  kameralösningar som låter användaren styra muspekaren med blicken eller huvudrörelser.
    </p>

    <p>
      I listan nedan presenteras länkar till tillgänglighetsinformation för några av de stora aktörerna och deras verktyg:
    </p>

    <ul>
      <li>
        <a href="https://www.google.com/accessibility/products-features.html" target="_blank">Google</a>
        <ul>
          <li>
            <a href="https://support.google.com/accessibility/android" target="_blank">Android Accessibility Hjälp</a>
          </li>
          <li>
            <a href="https://play.google.com/store/apps/details?id=com.google.android.marvin.talkback" target="_blank">Android Accessibility Suite</a>
          </li>
          <li>
            <a href="https://support.google.com/mail/answer/6115187" target="_blank">Använda tillgänglighetsfunktioner med Gmail</a>
          </li>
          <li>
            <a href="https://support.google.com/maps/answer/6396990" target="_blank">Tillgänglighet i Google Maps</a>
          </li>
          <li>
            <a href="https://support.google.com/websearch/answer/181196" target="_blank">Tillgänglighet i Google Sök</a>
          </li>
        </ul>
      </li>
      <li>
        <a href="https://www.apple.com/accessibility" target="_blank">Apple</a>
        <ul>
          <li>
            <a href="https://www.apple.com/accessibility/mac/" target="_blank">Mac</a>
          </li>
          <li>
            <a href="https://www.apple.com/accessibility/iphone/" target="_blank">iPhone</a>
          </li>
          <li>
            <a href="https://www.apple.com/accessibility/iPad/" target="_blank">iPad</a>
          </li>
        </ul>
      </li>
      <li>
        <a href="https://www.microsoft.com/en-us/accessibility" target="_blank">Microsoft</a>
        <ul>
          <li>
            <a href="https://www.microsoft.com/en-us/accessibility/windows" target="_blank">Windows</a>
          </li>
          <li>
            <a href="https://www.microsoft.com/en-us/accessibility/windows" target="_blank">Office</a>
          </li>
          <li>
            <a href="https://www.xbox.com/en-us/xbox-one/accessibility" target="_blank">Xbox</a>
          </li>
        </ul>
      </li>
    </ul>

    <p>
      För ökad tillgänglighet i webbläsaren erbjuds olika möjligheter till personlig anpassning. Utöver inbyggda möjligheter för enklare inställningar, såsom att ändra textstorlek och önskvärt typsnitt, erbjuds en stor mängd av användbara webbläsartillägg utvecklade av såväl företag som privatpersoner.
    </p>

    <p>
      Många webbläsartillägg är kostnadsfria men utbudet kan variera kraftigt mellan de olika webbläsarna. Som exempel finns för Google Chrome tillägg för <a href="https://chrome.google.com/webstore/detail/just-read/dgmanlpmmkibanfdgjocnabmcaclkmod" target="_blank">ökad läsbarhet</a>, <a href="https://chrome.google.com/webstore/detail/chromevox/kgejglhpjiefppelpmljglcjbhoiplfn" target="_blank">uppläsning med talsyntes</a> eller att använda röstigenkänning för <a href="https://chrome.google.com/webstore/detail/ribbing-reader/ipilhfnbdkfbnbnahplmimdilodohknp" target="_blank">automatisk textning av tal</a> i t.ex. klassrummet eller en konferens.
    </p>

    <p>
      I listan nedan presenteras länkar till tillgänglighetsinformation för några av de stora webbläsarna och tillgängliga tillägg:
    </p>

    <ul>
      <li>
        <a href="https://www.google.com/accessibility/products-features.html" target="_blank">Google Chrome</a>:
        <a href="https://chrome.google.com/webstore/category/ext/22-accessibility" target="_blank">Chrome Web Store</a>
      </li>

      <li>
        Apple Safari:
        <a href="https://safari-extensions.apple.com/" target="_blank">Safari Extensions</a>
      </li>

      <li>
        <a href="https://support.mozilla.org/en-US/kb/accessibility-features-firefox-make-firefox-and-we" target="_blank">Mozilla Firefox</a>:
        <a href="https://addons.mozilla.org/en-US/firefox/search/?q=accessibility" target="_blank">Firefox Add-ons</a>
      </li>

      <li>
        <a href="https://docs.microsoft.com/en-us/microsoft-edge/accessibility" target="_blank">Microsoft Edge</a>:
        <a href="https://www.microsoft.com/sv-se/store/collections/edgeextensions" target="_blank">Tillägg till Microsoft Edge</a>
      </li>

      <li>
        Opera:
        <a href="https://addons.opera.com/sv/" target="_blank">Opera-tillägg</a>
      </li>

    </ul>

    <p>
      För att verktygens funktionalitet ska fungera som tänkt på en webbplats förutsätts att sidan är väl strukturerad och följer standarder beskrivna i föregående kapitel.
    </p>

    <info-fields class="infoFields" id="testa-skarmlasning">

      <div class="excercise">

        <div class="hBox">
          Testa skärmläsning
        </div>

        <p>
          Använd datorns, webbläsarens eller ett webbläsartilläggs skärmläsningsfunktion för att få de två bilderna nedan beskrivna med talsyntes. För de flesta verktyg görs detta genom att klicka på bilden eller via tangentbordsnavigering.
        </p>

        <p>
          Den vänstra bilden har nödvändig alternativtext angiven, medan den högra bilden saknar sådan beskrivning.
        </p>

        <div class="cats">
          <img src="https://upload.wikimedia.org/wikipedia/commons/6/6a/S%C3%B6t_kattunge.JPG" alt="Ett fotografi på en söt kattunge. Det är skymning, bilden är tagen uppifrån, och katten tittar in i kameran.">
          <img src="https://upload.wikimedia.org/wikipedia/commons/6/6a/S%C3%B6t_kattunge.JPG" />
        </div>

        <div>
          Källa: <a href="https://commons.wikimedia.org/wiki/File:S%C3%B6t_kattunge.JPG" title="Wikimedia Commons: Söt kattunge" target="_blank">1qaz2 (Wikimedia Commons)</a>
        </div>

        <div class="hBox">
          DISKUTERA
        </div>

        <ul>
          <li>
            Vilken text läses upp för respektive bild?
          </li>
          <li>
            Varför är alternativtexter viktiga för en webbplats tillgänglighet?
          </li>
          <li>
            Vilka begränsningar kan publika datorer innebära för tillgängligheten?
          </li>
        </ul>

      </div>

    </info-fields>

    <h3>Talteknologi och röststyrning</h3>

    <p>
      Som en följd av den snabba <a-wiki c="Artificiell_intelligens">AI</a-wiki>-utvecklingen har talteknologi blivit allt bättre och finns idag tillgänglig i mycket av den teknik vi använder dagligen. Smarta telefoner går att styra med enkla röstkommandon för att exempelvis söka efter information, ringa en vän eller starta en applikation. I hemmet är smarta högtalare, såsom <a-wiki c="Amazon_Echo">Amazon Echo</a-wiki>, <a-wiki c="Google_Home">Google Home</a-wiki> och Apples <a-wiki c="HomePod"> HomePod</a-wiki>, allt mer populära, vilka gör det möjligt att med röstkommandon spela musik, läsa upp nyheterna, ställa in alarm och påminnelser, skapa listor, beställa mat och produkter, styra dina smarta-hem-produkter, och mycket mer. Vidare gör taligenkänning det möligt att automatiskt transkribera tal och erbjuda realtidstextning.
    </p>

    <p>
      Enligt Svenskarna med funktionshinder och internet använder <a href="https://internet.begripsam.se/#/stats/Dator%2520SMFOI%25202017/1100" target="_blank">var tredje blind person talsyntes/uppläsningsprogram</a> och nästan var fjärde döv person använder tal-till-text som datorhjälpmedel.
    </p>

    <media-container class="mediaContainer">
      <div>
        <div class="content">
          <iframe src="https://www.youtube.com/embed/FQn6aFQwBQU" width="100%" height="360" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe>
        </div>
        <div class="caption">
          Smarta högtalare såsom Amazon Alexa låter användaren utföra enklare uppgifter och styra smarta-hem-produkter med röstkommandon.
        </div>
        <div class="source">
          Källa: <a href="https://www.youtube.com/watch?v=FQn6aFQwBQU" title="Youtube: Amazon Echo - Now Available" target="_blank">amazon (Youtube)</a>
        </div>
      </div>
    </media-container>

    <p>
      Med talteknologi direkt inbyggd i webbläsaren är det möjligt att interagera med webbplatser med hjälp av rösten samt erbjuda uppläsning av text- och alternativt medieinnehåll utan att användaren behöver ha särskild hårdvara eller program installerat. Tekniken har öppnat upp för många spännande initiativ.
    </p>

    <p>
      I Nederländerna har föreningen Bartiméus Sonneherdt Vereniging, som arbetar med blinda och synskadade, skapat en tjänst <a href="https://scribit.tv" target="_blank">Scribit.tv</a> för crowdsourcing av syntolkning för YouTubeklipp. Efter att ha registrerat sig kan vem som helst skapa texter för syntolkning, vilka läses upp med talsyntes samtidigt som tillhörande video spelas. Webbplatsen och tjänsten är på Nederländska.
    </p>

    <p>
      Ett liknande svenskt inititiv är <a href="https://www.textamig.se/" target="_blank">Texta mig!</a> som erbjuder ett fritt verktyg för att automatiskt generera undertexter till videoklipp, med hjälp av taligenkänning. Vem som helst kan i verktyget ange webbadressen till ett videoklipp på YouTube eller Vimeo och återfå en tidskodad, redigerbar textfil. För många YouTubeklipp kan textfilen laddas upp och tillgängliggöras fritt för allmänheten, och för Vimeo kan användare ladda upp textfiler till sina egna videoklipp.
    </p>

    <p>
      <a href="https://www.ribbingreader.com/" target="_blank">Ribbing Reader</a> är ett fritt verktyg (utvecklat av Interaktiva rum som driver Mikoteket) som med Chromes inbyggda taligenkänning erbjuder automatisk transkribering och undertexter av tal, direkt i webbläsaren. Verktyget möjliggör för exempelvis pedagoger att i realtid texttolka (och framöver även spara) det som sägs för elever med nedsatt hörsel eller koncentrationssvårigheter, samt för tillgängliggörande av konferenser, föreläsningar, tv-sändningar etc.
    </p>

    <info-fields class="infoFields" id="sok-med-rosten">

      <div class="excercise">

        <div class="hBox">
          Sök med rösten
        </div>

        <p>
          För <a href="https://www.google.se/chrome/" target="_blank">Chrome</a>-användare: använd webbläsarens inbyggda taligenkänning för att söka med rösten på svenska eller engelska.
        </p>

        <ol>
          <li>
            Klicka på knappen <i>Sök med rösten</i>
          </li>
          <li>
            I eventuellt popup-fönster, godkänn att webbplatsen använder datorns mikrofon.
          </li>
          <li>
            Börja prata för att automatiskt fylla i sökfältet.
          </li>
          <li>
            När du har pratat klart öppnas en ny webbläsarflik med sökresultaten.
          </li>
        </ol>

        <p>
          På <a href="https://www.google.se" target="_blank">Googles egen söksida</a> erbjuds i Chrome motsvarande funktion genom att klicka på sökfältets mikrofonikon.
        </p>

        <button @focus="speechRun">Sök med rösten</button>
        <br />
        <search-component :query="speechResult"></search-component>

        <div class="hBox">
          DISKUTERA
        </div>

        <ul>
          <li>
            Hur är upplevelsen att söka med rösten?
          </li>
          <li>
            Upplever du att programmet förstår vad du säger?
          </li>
          <li>
            Hur kan detta och liknande verktyg hjälpa olika personer med funktionsnedsättning?
          </li>
        </ul>

      </div>

    </info-fields>

    <h3>Sociala berättelser</h3>

    <p>
      <a-wiki c="Social_berättelse">Sociala berättelser</a-wiki> och seriesamtal är korta berättelser skrivna, illustrerade eller på annat sätt är personligt anpassade för personer med olika funktionsnedsättningar, exempelvis autismspektrumstörningar. De används i avsikt att hjälpa personer att förstå olika sociala situationer eller för att förbereda inför nya sammankomster eller besök på nya platser. Sociala berättelser kan enkelt skapas genom att rita och skriva med papper och penna, med stöd av digitala verktyg med <a href="https://www.symbolbruket.se/symboler#tab_anvandning" target="_blank">färdiga symboler</a>, eller genom att utveckla interaktiva upplevelser, såsom mobilspelet <a href="http://habilitering.se/undra" target="_blank">Undra om habiliteringen</a>.
    </p>

    <p>
       Många myndigheter, institutioner och företag erbjuder <a-wiki c="Öppna_data">öppen data</a-wiki> som är fritt tillgänglig att använda i egna verktyg och program. Detta gör det möjligt att designa sociala berättelser vars innehåll är dynamiskt och automatiserat. Som exempel är mobilappen <a href="http://www.mfd.se/stod-och-verktyg/digitala-verktyg/appar/klader-efter-vader/" target="_blank">Kläder efter väder</a> kopplad till <a href="https://www.smhi.se" target="_blank">SMHI</a>s öppna data, och presenterar utifrån den aktuella väderprognosen bilder som hjälper användaren att välja rätt kläder.
    </p>

    <p>
      För att förbereda personer för kommande händelser och besök på nya platser kan <a-wiki c="Panorama_(fotografi)">360-graders panoramafotografi</a-wiki> användas för att skapa sociala berättelser. I bild- och ljudserien nedan används Google Street View och Spotify för att förbereda inför ett besök på Göteborgs konserthus. Serien kan med fördel användas i kombination med konserthusets egen sida på <a href="http://www.t-d.se/sv/TD2/Avtal/Vastra-Gotalandsregionen/Goteborgs-konserthus/Stenhammarsalen/" target="_blank">Tillgänglighetsdatabasen</a>.
    </p>

    <div id="streetviews">

      <h4>Morgondagens schema</h4>

      <h3>Vi besöker konserthuset</h3>

      <div>
        <iframe src="https://www.google.com/maps/embed?pb=!4v1542375859486!6m8!1m7!1sTgCc25PPiVsVYNFW9SglBw!2m2!1d57.6978660620437!2d11.98044406218641!3f224.80989742830636!4f-0.34192504412547464!5f0.7820865974627469" width="400" height="300" frameborder="0" style="border:0" allowfullscreen></iframe>
        <p>
          Vi åker buss till Götaplatsen
        </p>
      </div>

      <div>
        <iframe src="https://www.google.com/maps/embed?pb=!4v1542380430650!6m8!1m7!1sUIC8J44lixGiBbG7k3BmLg!2m2!1d57.69743705864988!2d11.97935989830136!3f163.1748472143182!4f4.432023339000139!5f0.7820865974627469" width="400" height="300" frameborder="0" style="border:0" allowfullscreen></iframe>
        <p>
          Vi kliver av vid Götaplatsen.
        </p>
      </div>

      <div>
        <iframe src="https://www.google.com/maps/embed?pb=!4v1542380343641!6m8!1m7!1sCAoSLEFGMVFpcE1lLVoyNEVuQnJKWkYtVC1WLTVwaU5uMWtVTURYNzVaVTZISlJH!2m2!1d57.6972456!2d11.9794274!3f179.6867011341374!4f11.068449962499912!5f0.7820865974627469" width="400" height="300" frameborder="0" style="border:0" allowfullscreen></iframe>
        <p>
          På torget utanför finns en staty.
        </p>
      </div>

      <div>
        <iframe src="https://www.google.com/maps/embed?pb=!4v1542376683722!6m8!1m7!1sCAoSLEFGMVFpcE05SGxrTklRS1VMN0JxekZpTFFiS0phOG1GWFhFOVdMQzVPWHQ1!2m2!1d57.69735557475205!2d11.97933014389635!3f139.97204935187963!4f3.9384887242199085!5f0.7820865974627469" width="400" height="300" frameborder="0" style="border:0" allowfullscreen></iframe>
        <p>
          Ibland är det många människor på torget.
        </p>
      </div>

      <div>
        <iframe src="https://www.google.com/maps/embed?pb=!4v1542376144582!6m8!1m7!1sCAoSLEFGMVFpcFAzajZlRU8tbnJqbmNoU2xuLVlDY25mZV9BSU9OTS1GN2JieEVJ!2m2!1d57.69690050313432!2d11.97904419392273!3f263.3584638569013!4f0!5f0.7820865974627469" width="400" height="300" frameborder="0" style="border:0" allowfullscreen></iframe>
        <p>
          Vi kommer att lyssna på musik i ett stort rum.
        </p>
      </div>

      <div>
        <iframe src="https://open.spotify.com/embed/track/0nQQzGnAMoANSe6McKOqgo" width="400" height="100" frameborder="0" allowtransparency="true" allow="encrypted-media"></iframe>
        <p>
          Såhär kan musiken låta.
        </p>
      </div>

      <div>
        <iframe src="https://www.google.com/maps/embed?pb=!4v1542376815579!6m8!1m7!1sCAoSLEFGMVFpcFBIUGpMcWpkOVNDZzFzWm5JREFPQnBaRkcxMlBVMHVZbExXalNO!2m2!1d57.6966944!2d11.9803763!3f71.11998756783049!4f9.714040562686648!5f0.7820865974627469" width="400" height="300" frameborder="0" style="border:0" allowfullscreen></iframe>
        <p>
          Efter konserten är det kväll och mörkt ute.
        </p>
      </div>

    </div>

    <p>
      Google Street View <a href="https://www.google.com/streetview/understand/" target="_blank">täcker en stor del</a> av Sveriges vägnät. Det är även möjligt att ladda upp egna 360- eller vanliga fotografier, samt att använda bilder från andra användare. Genom att använda <a-wiki c="VR-glasögon">VR-glasögon</a-wiki> kan den immersiva upplevelsen av att befinna sig på plats öka.
    </p>

    <p>
       VR kan även användas för program och spel utvecklade särskilt för personer med kognitiva funktionsnedsättningar, såsom i exemplet nedan där barn med autism får öva sig i olika sociala situationer, eller för att <a href="https://www.immersivt.se/kan-vr-behandla-psykisk-ohalsa/" target="_blank">behandla psykisk ohälsa</a>. Kostnadsfria datorprogram såsom <a href="https://unity3d.com/VR-and-AR-corner" target="_blank">Unity 3D</a> möjliggör (för något tekniskt kunniga personer) att <a href="https://www.immersivt.se/hur-vr-hjalper-sjuariga-imogen-som-har-williams-syndrom/" target="_blank">designa och utveckla egna VR-upplevelser</a> anpassade för olika syndrom. Tekniken bör dock användas med försiktighet och med en förståelse för hur personen kan komma att reagera. Vissa upplever VR som obehagligt och riskerar att bli <a href="https://www.immersivt.se/forskare-chalmers-vr-aksjuka/" target="_blank">åksjuka</a>.
    </p>

    <media-container class="mediaContainer">
      <div>
        <div class="content">
          <iframe src="https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/freethinksuperhuman/videos/1542258932568079/&show_text=0&width=560" width="560" height="315" style="border:none;overflow:hidden" scrolling="no" frameborder="0" allowTransparency="true" allowFullScreen="true"></iframe>
        </div>
        <div class="caption">
          VR-glasögon och -upplevelser kan användas för att låta barn med autism öva och förstå olika sociala situationer.
        </div>
        <div class="source">
          Källa: <a href="https://www.facebook.com/freethinksuperhuman/videos/1542258932568079/" title="Using Virtual Reality to Help Kids with Autism" target="_blank">Superhuman (Facebook)</a>
        </div>
      </div>
    </media-container>

    Med inspiration från sociala berättelser och datorspel kan personligt anpassade skoluppgifter designas för att hjälpa barn och unga med neuropsykiatriska funktionsnedsättningar att klara av sin utbildning. <a href="https://spellararen.wordpress.com" target="_blank">SpelLäraren</a> använder bland annat <a-wiki c="Spelifiering">spelifiering</a-wiki> för att, med enkla medel, skapa <a href="https://gratisiskolan.se/blog/item/kan-spel-skapa-lust-till-larande/" target="_blank">personligt anpassade skolövningar</a> innehållande elevens favoritspelkaraktärer. Genom att göra om veckoschemat till ett spelschema eller att i matematiken ställa olika karäktärer mot varandra (för att öva de fyra räknesätten) känner sig eleverna inkluderade och inspireras till det egna skolarbetet.

    <info-fields class="infoFields" id="rakna-matte-pa-slagfaltet">

      <div class="excercise">

        <div class="hBox">
          Räkna matte på slagfältet
        </div>

        <p>
          Inspirerad av <a href="https://gratisiskolan.se/blog/item/kan-spel-skapa-lust-till-larande/" target="_blank">SpelLärarens övningar</a> kan du nedan ställa upp orcer mot alver på ett slagfält. För skolbarn med koncentrationssvårigheter kan personligt anpassade övningar baserade på det egna intresset, t.ex. fantasydatorspel, skapa intresse för skoluppgifterna. Eleven använder och övar grundläggande matematikkunskaper, kanske utan att själv vara medveten om det.
        </p>

        <Combat></Combat>

        <div class="hBox">
          DISKUTERA
        </div>

        <ul>
          <li>
            Vad innebär ett positivt/negativt resultat på slagfältet?
          </li>
          <li>
            Hur kan personligt anpassade övningar skapa intresse för olika elever med koncentrationssvårigheter?
          </li>
          <li>
            Hur kan liknande övningar designas för att öva grundläggande kunskaper, även inom andra skolämnen?
          </li>
        </ul>

      </div>

    </info-fields>

    <h3>Tillgänglig kultur</h3>

    <p>
      Ovan exempel på digital teknik har till stor del fokuserat på att hjälpa personer klara av vardagen. Men det är viktigt att även andra områden, såsom möjligheten att ta del av nyheter, samhällsinformation och kulturliv, tillgängligörs för personer med särskilda behov. <a href="https://www.mtm.se/" target="_blank">Myndigheten för tillgängliga medier</a> arbetar för att alla ska ha tillgång till litteratur och samhällsinformation utifrån var och ens egna förutsättningar, oavsett läsförmåga eller funktionsnedsättning, och erbjuder bland annat det digitala bilioteket <a href="http://www.legimus.se" target="_blank">Legimus</a> och nyhetssidan <a href="http://8sidor.se/" target="_blank">8 Sidor</a> med nyheter på lättläst svenska.
    </p>

    <p>
      För tillgänglighet inom museisektorn var <a href="http://www.funktek.se" target="_blank">Funktek</a> ett treårigt Göteborgsbaserat projekt som arbetade för allas lika rätt att delta i och skapa kulturen. Med <i>alla</i> museibesökare i åtanke anställdes personer med olika funktionsvariationer, och tillsammans designade projektgruppen olika workshops för att utvärdera tillgängligheten på Göteborgs stadsmuseum. En utgångspunkt var att <i>museet har funktionshinder - inte besökarna</i>.
    </p>

    <p>
      När <a href="https://www.tekniskamuseet.se" target="_blank">Tekniska Museet</a> i Stockholm designade och utvecklade utställningen <a href="https://www.tekniskamuseet.se/upplev/utstallningar/megamind/" target="_blank">MegaMind</a> gjordes det med användaren och tillgänglighet i fokus. Utöver att designa för läsbarhet och framkomlighet var det även viktigt att de digitala och interaktiva installationerna inte diskrimerade användaren utifrån exempelvis begränsad tal- eller rörelseförmåga. Installationen Dansa med synapser (utvecklad av <a href="https://www.tii.se/projects/dance-with-synapses" target="_blank">RISE Interactive</a> och Interaktiva rum som driver Mikoteket) använder en <a-wiki c="https://sv.wikipedia.org/wiki/Kinect">Kinectkamera</a-wiki> för att känna av besökarnas positioner och låter dem använda kroppen för att interagera med och styra programmet. Vid den tekniska utvecklingen säkertställdes att tekniken inte förutsatte vissa rörelsemönster och att även rullstulsanvändare kan använda installationen, på lika villkor. En gång i månaden anordnas <a href="https://www.tekniskamuseet.se/skolprogram/megamind-funkis-extra-tillgangliga-onsdagar" target="_blank">MegaMind Funkis</a>, ett tillfälle för skolgrupper med olika funktionsvariationer att besöka museet i lugn och ro.
    </p>

    <p>
      På <a href="https://www.akvarellmuseet.org" target="_blank">Nordiska Akvarellmuseet</a> på Tjörn drivs projektet <a href="https://www.akvarellmuseet.org/projekt/distansbesok-pa-konstmuseum" target="_blank">Distansbesök på konstmuseum</a> tillsammans med <a href="https://www.tii.se/projects/en-robot-besoker-museum" target="_blank">RISE Interactive</a>, med syfte att ge personer som har svårt att fysiskt besöka museet en chans till ett möte och museivisning i realtid. Distansvisning med hjälp av en robot, till skillnad från exempelvis inspelad visning, möjliggör aktivt deltagande och samtal, och strävar att efterlikna en visning på plats. Rummet tas in, konstpedagog och deltagare kan se varandra via skärmen och ställa frågor, flika in och samtala kring konsten man gemensamt ser.
    </p>

    <p>
      På National Theatre i Storbrittanien erbjuds för vissa föreställningar <a-wiki c="Förstärkt_verklighet">AR</a-wiki>-glasögon för att ge automatiskt synkroniserade <a href="https://www.nationaltheatre.org.uk/your-visit/access/caption-glasses" target="_blank">undertexter till personer som är döva eller med nedsatt hörsel</a>. Detta möjliggör för åskådaren att bättre följa skådespelarna på scen, se deras ansiktsuttryck och läsa deras läppar, och bidrar sammantaget till en starkare teaterupplevelse. Även användningen av ljud kan förstärka kulturupplevelser för döva personer. På biografer är användningen av lågfrekventa ljud, som känns i kroppen som vibrationer, ett effektivt sätt att skapa extra känsla i filmer. Framförallt action- och skräckgenren är idag duktiga på att uttnyttja detta, men även inom andra genrer kan tekniken användas för att <a href="https://www.dn.se/kultur-noje/nytt-filmljud-far-horselskadade-att-kanna-skracken/" target="_blank">förhöja upplevelsen för döva och hörselskadade</a>.
    </p>

    <p>
      Inom datorspelkulturen krävs ofta snabba reflexer och fullt fungerande motorik för att praktiskt kunna delta. Användaren behöver simultant kunna styra mus och tangentbord eller använda en handhållen spelkontroll med joystick och många små knappar. För personer med motoriska funktionshinder är dessa kontroller i många fall omöjliga att använda. Microsoft har uvecklat
      <a href="https://www.xbox.com/en-US/xbox-one/accessories/controllers/xbox-adaptive-controller" target="_blank">Xbox Adaptive Controller</a>, en spelkontroll med stora fysiska knappar och stora anpassningmöjligheter för styrning med såväl händer, fötter, axlar eller andra kroppsdelar.
    </p>
    <p>

      <a-wiki c="Förstapersonsskjutare">FPS</a-wiki>-spelaren <a href="https://www.youtube.com/watch?v=ZMvikz2cA-8" target="_blank">RockyNoHands</a> använder en <a href="https://www.quadstick.com/" target="_blank">Quadstick</a> med munjoystick och sensorer för sug- och utblås, en spelstyrenhet utvecklad för personer med <a-wiki c="Tetraplegi">tetraplegi</a-wiki>, nedsatt eller förlorad sensorisk eller motorisk funktion som följd av ryggmärgsskada. RockyNoHands har nästan 50.000 följare på <a href="https://www.twitch.tv/rockynohands" target="_blank">Twitch</a> och de anpassade verktygen möjliggör för honom och många andra spelare att besegra motståndare med fullt fungerande motorik och traditionell spelutrustning.
    </p>

    <p>
      I forskningsprojektet <a href="https://www.youtube.com/watch?v=bGgd0x31D1I" target="_blank">Kinect Wheels</a> används en Kinectkamera för att låta rullstolsburna personer styra bil- och racingspel. Kameratekniken innebär att spelaren inte behöver bära någon särskild teknisk utrustning, utan styr spelet genom att förflytta och rotera sin egen rullstol och kropp.
    </p>

    <info-fields class="infoFields">

      <div class="excercise">

        <div class="hBox">
          Vilken kultur konsumerar du?
        </div>

        <p>
          Reflektera över den kultur du själv gillar att kommunicera eller aktivt delta i?
        </p>

        <div class="hBox">
          DISKUTERA
        </div>

        <ul>
          <li>
            Vilken kultur gillar du själv att konsumera eller aktivt delta i?
          </li>
          <li>
            Vad krävs av dig som konsument eller deltagare, för en fullvärdig upplevelse?
          </li>
          <li>
            Vilka personer kan ha svårt att delta eller få en fullvärdig upplevelse?
          </li>
          <li>
            Vilka digitala verktyg finns, eller kan designas, för ökad tillgänglighet för att inkludera fler?
          </li>

        </ul>

      </div>


      <div class="more">

        <div class="hBox">
          FÖRDJUPNING
        </div>

        <ul>
          <li>
            FunkTek har publicerat flera <a href="http://www.funktek.se/handbocker/" target="_blank">handböcker med samlade tankar, erfarenheter och lärdomar</a> kring sitt arbete med tillgängliga museum. Handböckerna är utmärkt kunskap för tillgängliggörande även av andra publika miljöer.
          </li>
        </ul>

      </div>

    </info-fields>


  </div>
</template>

<script>

//import cb from '@/assets/script/CommandBot.js'

import InfoFields from '@/components/learn/InfoFields.vue'
import MediaContainer from '@/components/learn/MediaContainer.vue'
import SearchComponent from '@/components/learn/SearchComponent.vue'
import SoiGraph from '@/components/SoiGraph.vue'
import SlideComponent from '@/components/learn/SlideComponent.vue'
import AWiki from '@/components/AWiki.vue'
import AWikt from '@/components/AWikt.vue'
import RandomString from '@/components/RandomString.vue'
import Combat from '../components/Combat.vue'


export default {
  components: {
    InfoFields,
    MediaContainer,
    SearchComponent,
    SoiGraph,
    SlideComponent,
    AWiki,
    AWikt,
    RandomString,
    Combat
  },
  data () {
    return {
      'header': 'Digitala hjälpmedel',
      wsr: "",
      speechResult: "",
      jitsi: ""
    }
  },
  methods: {
  },
  created: function () {
    /*let scr = document.createElement('script')
    scr.setAttribute('src', 'https://www.interaktivarum.se/skolverket/commandbot.js')
    document.head.appendChild(scr)*/
  },
  mounted: function () {
    /*this.bot = new cb.CommandBot;

    //Välj språk för taligenkänning och uppläsning
    //För engelska: ändra till "en-US"
    this.bot.setLanguageRecognition("sv-SE");
    this.bot.setLanguageSynthesis("sv-SE");

    //Välj startkommando/aktiveringsord
    this.bot.setKeyword("Ribbing");

    this.bot.addCommand("vem är du", this.about);
    this.bot.addCommand("skämt,rolig historia", this.tellJoke);
    //this.bot.addCommand("nyhet,nyheter,nyheten,nyheterna", readNews);

    //Starta boten
    this.bot.run();

    this.about();*/
    this.initSpeech();

  },
  methods: {
    initSpeech: function () {
      this.wsr = new webkitSpeechRecognition();
      this.wsr.continuous = false;
  	  this.wsr.interimResults = true;
      this.wsr.onresult = this.onSpeechResult;
    },
    onSpeechResult: function (event) {
      for (var i = event.resultIndex; i < event.results.length; ++i) {

        this.speechResult = event.results[i][0].transcript;

	      if (event.results[i].isFinal) { //Final results
          window.open("https://www.google.com/search?q=" + this.speechResult);
          this.wsr.stop();
	      }

	    }
    },
    speechRun: function () {
      this.speechResult = "";
      this.wsr.start();
    }
  }
}
</script>

<style scoped>

#streetviews{
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 400px;
  overflow: scroll;
  text-align: center;
  font-weight: 400;
  background: rgb(230,230,230);
}

#streetviews div{
  flex: 0 1 100px;
  background: white;
  border: 1px solid gray;
  margin: 5px;
  height: auto;
  width: 400px;
}

.cats{
}

.cats > *{
  width: 200px;
}

</style>



// WEBPACK FOOTER //
// src/components/learn/tracks/accessibility/chapters/utilities.vue